"use client"

import React, { useState, useEffect, useRef, useMemo, Component } from "react"
import $ from "jquery"

import {
  ArrowLeftIcon, DownloadIcon, Edit, Heart, MoreVertical, Play, SaveIcon,
  Trash2, Search, Plus, Eye, Star, PlusIcon, Upload, Loader2,
  ChevronDown, ChevronUp, Grid2X2, List, X, Edit2, Download, Package, Layers, ClipboardList
} from "lucide-react"

import { Button } from "./ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu"
import { Badge } from "./ui/badge"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { fabric } from "fabric"
import { jsPDF } from "jspdf"
import DynamicList from "../js/simulador/DynamicList"

import {
  deleteRegisteredObj, saveBaseModel, updateClientModelsSimulator,
  updateClientPath, editRegisteredNameSimulator, edit3D, registerAPI, getClient,
  getMySaved3dsModular, loadModularSetup, deleteModularSetup, getUserId
} from "../API"

import { sampleProjects } from "./sampleProjectsData"
import BriefingModal from "./BriefingModal"
import BriefingsPage from "./BriefingsPage"
import { useBriefingNotifications } from "./useBriefingNotifications"

// ─── Error Boundary ──────────────────────────────────────────────────────────

interface EBState { hasError: boolean; message: string }

class ChangeSportErrorBoundary extends Component<{ children: React.ReactNode }, EBState> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false, message: "" }
  }
  static getDerivedStateFromError(error: Error): EBState {
    return { hasError: true, message: error?.message ?? "Erro desconhecido" }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ChangeSport] Error caught by boundary:", error, info)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-primary/60 p-8">
          <div className="text-4xl">⚠️</div>
          <p className="text-base font-semibold text-primary">Algo deu errado</p>
          <p className="text-sm text-center max-w-sm">{this.state.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, message: "" })}
            className="mt-2 rounded-lg bg-blue-600 px-5 py-2 text-sm text-white hover:bg-blue-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ─── Safe string helper ───────────────────────────────────────────────────────
const safeStr = (v: any): string => (v == null ? "" : String(v))

// ─── Types ──────────────────────────────────────────────────────────────────

interface Project {
  id: string
  name: string
  category: string
  thumbnail: string
  createdAt: string
  path: string
  custom: boolean
  model_id?: any
  wiggle?: boolean
  metal?: any
  brilho?: any
}

interface Model {
  id: string
  name: string
  desc: string
  thumbnail: string
  isDefault: boolean
  path: string
  simulator: boolean
  category: string
  sizes?: string
  price?: string
  discount?: string
  collar?: string
  sleeve?: string
  escudos?: { nome: string; url?: string; preview?: string }[]
  tecidos?: { nome: string; url?: string; preview?: string }[]
  form: boolean
  thumbnailUpdate?: boolean
}

// ─── Sidebar categories (shared between tabs) ────────────────────────────────

const SIMULATOR_CATEGORIES = [
  { key: "Todos", label: "Todos", icon: "🏠" },
  { key: "golas", label: "Golas", icon: "⚽" },
  { key: "feminino", label: "Feminino", icon: "👚" },
  { key: "sisbolt_novos_mockups", label: "Sisbolt", icon: "👕" },
  { key: "Modular - Superior", label: "Modular - Superior", icon: "👕" },
  { key: "Modular - Inferior", label: "Modular - Inferior", icon: "👖" },
  { key: "Animados Novos", label: "Animados Novos", icon: "🎬" },
  { key: "Favoritos", label: "Favoritos", icon: "❤️" },
  { key: "Simulador", label: "Simulador", icon: "🎮" },
  { key: "Animados_M", label: "Animados_M", icon: "🧍" },
  { key: "Animados_F", label: "Animados_F", icon: "🧍‍♀️" },
  { key: "Banner", label: "Banner", icon: "🖼️" },
  { key: "Boné", label: "Boné", icon: "🧢" },
  { key: "Novos", label: "Novos", icon: "✨" },
  { key: "Novos2", label: "Novos2", icon: "✨" },
  { key: "Novos3", label: "Novos3", icon: "✨" },
  { key: "canecas", label: "Canecas", icon: "☕" },
  { key: "cgtrader", label: "CG Trader", icon: "📦" },
  { key: "bases_novas_física", label: "Bases Novas Fisica", icon: "🔧" },
  { key: "webster", label: "Webster", icon: "🌐" },
  { key: "fifa", label: "Fifa", icon: "⚽" },
  { key: "kits", label: "Kits", icon: "🎽" },
  { key: "pesca_e_surf", label: "Pesca e Surf", icon: "🏄" },
  
]

const MANAGER_CATEGORIES = [
  { key: "Tudo", label: "Tudo", icon: "🏠" },
  { key: "Modular - Superior", label: "Modular - Superior", icon: "👕" },
  { key: "Modular - Inferior", label: "Modular - Inferior", icon: "👖" },
  { key: "Animados Novos", label: "Animados Novos", icon: "🎬" },
  { key: "Favoritos", label: "Favoritos", icon: "❤️" },
  { key: "Simulador", label: "Simulador", icon: "🎮" },
  { key: "Animados_M", label: "Animados_M", icon: "🧍" },
  { key: "Animados_F", label: "Animados_F", icon: "🧍‍♀️" },
  { key: "Banner", label: "Banner", icon: "🖼️" },
  { key: "Boné", label: "Boné", icon: "🧢" },
  { key: "Novos", label: "Novos", icon: "✨" },
  { key: "Novos2", label: "Novos2", icon: "✨" },
  { key: "Novos3", label: "Novos3", icon: "✨" },
  { key: "Canecas", label: "Canecas", icon: "☕" },
  { key: "CG Trader", label: "CG Trader", icon: "📦" },
  { key: "Bases Novas Fisica", label: "Bases Novas Fisica", icon: "🔧" },
  { key: "Webster", label: "Webster", icon: "🌐" },
  { key: "Fifa", label: "Fifa", icon: "⚽" },
  { key: "Kits", label: "Kits", icon: "🎽" },
  { key: "Pesca e Surf", label: "Pesca e Surf", icon: "🏄" },
]

// ─── Window setup ────────────────────────────────────────────────────────────

;(window as { [key: string]: any }).sampleProjects = sampleProjects

type ChangeSportProps = { canvas: HTMLCanvasElement }

// ─── Main component ──────────────────────────────────────────────────────────

const ChangeSport = ({ canvas }: ChangeSportProps) => {

  // ── Global screen tab ──
  const [mainTab, setMainTab] = useState<"simulador" | "gerenciador">("gerenciador")

  // ── Shared UI state ──
  const [searchTerm, setSearchTerm] = useState("")
  const [activeCategory, setActiveCategory] = useState("Todos")
  const [sortOrder, setSortOrder] = useState("Mais recentes")
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [categoriesOpen, setCategoriesOpen] = useState(true)
  const [selectedItem, setSelectedItem] = useState<Project | Model | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [briefingModalModel, setBriefingModalModel] = useState<Model | null>(null)
  const [showBriefingsPage, setShowBriefingsPage] = useState(false)
  const briefingUnreadCount = useBriefingNotifications()

  // ── Cargo do usuário logado, pra simplificar a tela pro vendedor ──
  const [userRole, setUserRole] = useState<string | null>(null)
  useEffect(() => {
    (async () => {
      try {
        const client = await getClient(getUserId())
        if (client && client[0]) setUserRole(client[0].role || null)
      } catch (e) {
        console.error("Erro ao buscar cargo do usuário:", e)
      }
    })()
  }, [])
  const isVendedor = userRole === "vendedor"
  const isDesigner = userRole === "designer"
  const isGestao = userRole === "gestao"
  useEffect(() => { if (isVendedor && !isGestao) setMainTab("gerenciador") }, [isVendedor])

  // ── Setup Modular state ──
  const [modularSetups, setModularSetups] = useState<Model[]>([])
  const [modularParts, setModularParts] = useState<any[]>([])
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false)
  const [setupStep, setSetupStep] = useState<"select" | "position">("select")
  const [setupName, setSetupName] = useState("")
  const [selectedSetupModels, setSelectedSetupModels] = useState<Model[]>([])

  const loadModularSetups = async () => {
    const data = await getMySaved3dsModular()
    const mapped: Model[] = data.map((item: any) => ({
      id: String(item.id),
      name: item.name,
      desc: item.desc || "",
      thumbnail: item.thumb2 || "",
      isDefault: false,
      path: item.path || "",
      simulator: false,
      category: "Setup Modular",
      form: false,
      tecidos: [],
      escudos: [],
    }))
    setModularSetups(mapped)
  }

  useEffect(() => { loadModularSetups() }, [])

  useEffect(() => {
    if (selectedItem && (selectedItem as Model).category === "Setup Modular") {
      const loadParts = async () => {
        const data = await (window as any).getModularSetupParts?.()
        if (data) setModularParts(data)
        else {
          const setupData = await loadModularSetup((selectedItem as Model).id)
          if (setupData?.setup_data) setModularParts(setupData.setup_data)
        }
      }
      loadParts()
    } else {
      setModularParts([])
    }
  }, [selectedItem?.id])

  const handleCreateSetup = () => {
    setSetupStep("select")
    setSetupName("")
    setSelectedSetupModels([])
    setIsSetupModalOpen(true)
  }

  const handleToggleSetupModel = (model: Model) => {
    setSelectedSetupModels(prev => {
      const exists = prev.find(m => m.id === model.id)
      if (exists) return prev.filter(m => m.id !== model.id)
      return [...prev, model]
    })
  }

  const handleStartPositioning = () => {
    if (selectedSetupModels.length === 0) return
    setSetupStep("position")
    setIsSetupModalOpen(false)
    // Adiciona cada modelo na cena
    for (const model of selectedSetupModels) {
      (window as any).add_mode = true;
      let id = "0"
      if ((window as any).groupMore)
        id = String((window as any).groupMore.children.length)
      ;(window as any).sceneAPI?.addObject({ id, name: model.name.replace("SIMULADOR -", ""), type: "Mesh" })
      const svgPath = model.path.replace(".glb", ".svg")
      ;(window as any)["loadParts"](null, model.path, true, 1, false, true, svgPath, null)
    }
  }

  const handleOpenModularSetup = async (model: Model) => {
    ;(window as any)["open_modular_setup"](model.id)
    ;(window as any).setActiveTool?.('select')
  }

  const handleDeleteModularSetup = async (model: Model) => {
    if (window.confirm("Deletar este setup modular?")) {
      await deleteModularSetup(model.id)
      setModularSetups(prev => prev.filter(m => m.id !== model.id))
    }
  }

  // ── Simulator (ChangeSport) state ──
  const [projects, setProjects] = useState<Project[]>(sampleProjects as Project[])
  const [favorites, setFavorites] = useState<string[]>([])
  // ── New Model modal state ──
  const [isNewModelOpen, setIsNewModelOpen] = useState(false)
  const [isNewModelLoading, setIsNewModelLoading] = useState(false)
  const [newModelForm, setNewModelForm] = useState<any>({
    name: "", desc: "", category: "", sizes: "", price: "", discount: "",
    collar: "", sleeve: "", obj: null, svg: null, thumbnail: null,
    tecidos: [], escudos: [], form: false,
  })

  const resetNewModelForm = () => setNewModelForm({
    name: "", desc: "", category: "", sizes: "", price: "", discount: "",
    collar: "", sleeve: "", obj: null, svg: null, thumbnail: null,
    tecidos: [], escudos: [], form: false,
  })

  const isNewModelValid = () => !!newModelForm.obj && !!newModelForm.svg

  const handleSaveNewModel = async () => {
    if (!isNewModelValid()) return
    setIsNewModelLoading(true)
    try {
      const url = URL.createObjectURL(newModelForm.obj)
      const thumb: string = await (window as any)["generateGLBThumbnail"](url)
      const blob: Blob = await (await fetch(thumb)).blob()
      await registerAPI(newModelForm, "3ds", true, blob)
      resetNewModelForm()
      setIsNewModelOpen(false)
    } catch (err) {
      console.error("[handleSaveNewModel]", err)
      alert("Erro ao salvar modelo. Tente novamente.")
    } finally {
      setIsNewModelLoading(false)
    }
  }
  // Start above the highest static ID (341) to avoid collisions with sampleProjects
  const modelCountRef = useRef(
    Math.max(0, ...sampleProjects.map((p) => parseInt(p.id) || 0)) + 1
  )

  // ── Manager (ModelManager) state ──
  const [models, setModels] = useState<Model[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<any>(null)
  const [activeManagerTab, setActiveManagerTab] = useState<"Todos" | "Simulador" | "Favoritos">("Todos")
  const bulkFileInputRef = useRef<HTMLInputElement>(null)
  const [bulkUploadModel, setBulkUploadModel] = useState<Model | null>(null)
  const [bulkUploadStatus, setBulkUploadStatus] = useState({ active: false, current: 0, total: 0, modelName: "" })

  // ─── Close menus on outside click ────────────────────────────────────────
  useEffect(() => {
    const close = () => { setOpenMenuId(null); setShowSortMenu(false) }
    document.addEventListener("click", close)
    return () => document.removeEventListener("click", close)
  }, [])

  // Reset search/category when switching tabs
  useEffect(() => {
    setSearchTerm("")
    setActiveCategory(mainTab === "simulador" ? "Todos" : "Tudo")
    setSelectedItem(null)
  }, [mainTab])

  // ─── Simulator filtering ─────────────────────────────────────────────────
  const filteredProjects = useMemo(() => {
    let list = projects
    if (activeCategory === "Favoritos") {
      list = projects.filter((p) => favorites.includes(p.id))
    } else if (activeCategory !== "Todos") {
      list = projects.filter((p) => p.category === activeCategory)
    }
    if (searchTerm) {
      const term = safeStr(searchTerm).toLowerCase()
      list = list.filter((p) => safeStr(p.name).toLowerCase().includes(term))
    }
    // Deduplicate by id to avoid React key warnings and rendering glitches
    const seen = new Set<string>()
    list = list.filter((p) => { if (seen.has(p.id)) return false; seen.add(p.id); return true })
    return list
  }, [projects, activeCategory, favorites, searchTerm])

  // ─── Manager filtering ───────────────────────────────────────────────────
  const filteredModels = useMemo(() => {
    const seen = new Set<string>()
    return models.filter((model) => {
      if (seen.has(model.id)) return false
      seen.add(model.id)
      const term = safeStr(searchTerm).toLowerCase()
      const matchSearch = safeStr(model.name).toLowerCase().includes(term)
      const matchTab =
        activeManagerTab === "Todos" ? true :
        activeManagerTab === "Simulador" ? model.simulator :
        activeManagerTab === "Favoritos" ? model.isDefault : true
      const matchCat =
        activeCategory === "Tudo" ? true :
        activeCategory === "Favoritos" ? model.isDefault :
        activeCategory === "Simulador" ? model.simulator :
        (model.category || "").toLowerCase() === safeStr(activeCategory).toLowerCase()
      return matchSearch && matchTab && matchCat
    })
  }, [models, searchTerm, activeManagerTab, activeCategory])

  // ─── Window handlers — Simulator ─────────────────────────────────────────

  ;(window as any).setFavorites = (savedFavorites: any) => setFavorites(savedFavorites)

  ;(window as { [key: string]: any })["toggleFavorite"] = (projectId: string) => toggleFavorite(projectId)

  ;(window as { [key: string]: any })["addModel"] = (name: string, thumb: string, obj: string, model_id: any) => {
    modelCountRef.current += 1
    const newId = String(modelCountRef.current)
    setProjects((prev) => {
      // Avoid adding if ID already exists
      if (prev.some((p) => p.id === newId)) return prev
      return [...prev, {
        id: newId, name, category: "Registrado",
        thumbnail: thumb, createdAt: new Date().toLocaleDateString("pt-BR"),
        path: obj, custom: true, model_id,
      }]
    })
  }

  useEffect(() => { /* favorites sync side-effect placeholder */ }, [favorites])

  const toggleFavorite = (projectId: string) => {
    setFavorites((prev) => {
      const updated = prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
      localStorage.setItem("favorites", JSON.stringify(updated))
      if (updated.includes(projectId)) {
        const project = projects.find((obj) => obj.id === projectId) as any
        ;(window as { [key: string]: any })["handleAddModel"](
          project.id, "SIMULADOR - " + project.name, project.thumbnail,
          false, "./models/novos/" + project.path + ".glb", true
        )
      } else {
        ;(window as { [key: string]: any })["handleRemoveModel"](projectId)
      }
      updateClientModelsSimulator(updated)
      return updated
    })
  }

  const deleteProject = (projectId: string, projectPath: string, projectName: string, projectModelID: any) => {
    deleteRegisteredObj(
      projectModelID,
      projectPath.replace("https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/3ds/", "")
    )
    setProjects((prev) => prev.filter((p) => p.id !== projectId))
    setFavorites((prev) => prev.filter((id) => id !== projectId))
  }

  const openProject = async (projectId: string, projectPath: string, projectName: string, model_id: any, metal: any, brilho: any, category: any, wiggle: any) => {
    ;(window as { [key: string]: any }).currentCategory = category
    ;(window as { [key: string]: any }).currentMetal = metal
    ;(window as { [key: string]: any }).wiggle = wiggle
    $(".obj-type").prop("checked", true)
    $("#model-title span").text(projectName)
    var path = "./models/novos/" + projectPath + ".svg"
    if (projectPath.includes("http")) path = projectPath.replace(".glb", ".svg")
    ;(window as { [key: string]: any })["loadParts"](null, projectPath, true, 1, false, true, path, model_id, metal, brilho)
  }

  const [savedTocatalog, setSavedTocatalog] = useState<Set<string>>(new Set())

  const handleSaveModel = async (project: any) => {
    if (savedTocatalog.has(project.id)) return
    const path = project.thumbnail.replace("webp", "")
    if ((window as any).confirm("Adicionar modelagem ao catálogo da loja?")) {
      await saveBaseModel(path)
      // Mark as saved locally
      setSavedTocatalog(prev => new Set(prev).add(project.id))
      // Immediately add to manager models list so it appears without reload
      const addFn = (window as any).handleAddModel
      if (typeof addFn === "function") {
        addFn(
          project.id,
          project.name,
          project.thumbnail,
          false,
          project.path.includes("http") ? project.path : "./models/novos/" + project.path + ".glb",
          false,
          "",
          project.category ?? "",
          "", "", "", "", "",
          [], [], false
        )
      } else {
        // fallback: update models state directly
        setModels(prev => {
          if (prev.some(m => String(m.id) === String(project.id))) return prev
          return [...prev, {
            id: String(project.id),
            name: project.name,
            desc: "",
            thumbnail: project.thumbnail,
            isDefault: false,
            path: project.path.includes("http") ? project.path : "./models/novos/" + project.path + ".glb",
            simulator: false,
            category: project.category ?? "",
            form: false,
            tecidos: [],
            escudos: [],
          }]
        })
      }
    }
  }

  const handleDownloadOnly = (projectPath: string) => {
    const a = document.createElement("a")
    a.href = "./models/novos/" + projectPath + ".svg"
    a.download = "meu-svg.svg"
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }



  // ─── Window handlers — Manager ───────────────────────────────────────────

  ;(window as any).handleAddModel = async (
    id: string, name: string, thumb: string, isDefault: boolean, path: any,
    simulator: any, desc: any, category: any, sizes: any, price: any,
    discount: any, collar: any, sleeve: any, tecidos: any, escudos: any, form: any
  ) => {
    try {
      setModels(prev => [...prev, {
        id: safeStr(id),
        name: safeStr(name),
        desc: safeStr(desc),
        category: safeStr(category),
        sizes: safeStr(sizes),
        price: safeStr(price),
        discount: safeStr(discount),
        collar: safeStr(collar),
        sleeve: safeStr(sleeve),
        thumbnail: safeStr(thumb),
        isDefault: !!isDefault,
        path: safeStr(path),
        simulator: !!simulator,
        tecidos: tecidos ?? [],
        escudos: escudos ?? [],
        form: !!form,
      }])
    } catch (err) {
      console.error("[handleAddModel] failed:", err)
    }
  }

  ;(window as any).handleRemoveModel = (id: string | number) => {
    setModels(prev => prev.filter(m => String(m.id) !== String(id)))
  }

  useEffect(() => {
    const onUpdateName = (e: Event) => {
      const { id, newName } = (e as CustomEvent).detail || {}
      setModels((prev) => prev.map((m) => String(m.id) === String(id) ? { ...m, name: String(newName) } : m))
    }
    window.addEventListener("modelmanager:updateName", onUpdateName)
    return () => window.removeEventListener("modelmanager:updateName", onUpdateName)
  }, [])

  const handleDeleteModel = async (id: string, simulator: any, path: any) => {
    if (window.confirm("Deseja Deletar esse modelo?")) {
      if (!simulator) {
        const ok = await deleteRegisteredObj(id, path.replace("https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/3ds/", ""))
        if (ok) setModels(models.filter((m) => m.id !== id))
      }
    }
  }

  const handleUnmarkModel = async (id: string, simulator: any, path: any) => {
    if (window.confirm("Deseja Remover esse modelo dos favoritos?")) {
      if (localStorage.getItem("favorites")) {
        const updated = JSON.parse(localStorage.getItem("favorites") as any)
        const index = updated.indexOf(id)
        if (index > -1) updated.splice(index, 1)
        ;(window as { [key: string]: any })["toggleFavorite"](id)
        setModels(models.filter((m) => m.id !== id))
      }
    }
  }

  const handleSetDefaultModel = (id: string, path: any) => {
    setModels(models.map((m) => ({ ...m, isDefault: m.id === id })))
    updateClientPath(path)
  }

  const handleStartEdit = (model: any) => {
    const SUPABASE_TECIDOS = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/tecidos/"
    const SUPABASE_ESCUDOS = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/escudos/"
    const mapItems = (items: any[], baseUrl: string) => {
      if (!items || !Array.isArray(items)) return []
      return items.map((item: any) => {
        const key = Object.keys(item)[0]; const path = item[key]
        return { nome: key.replace(/\d+$/, ""), preview: baseUrl + path, file: null, existingUrl: baseUrl + path, originalKey: key }
      })
    }
    setEditingModel({
      id: model.id, name: model.name, desc: model.desc, glb: null, svg: null,
      path: model.path, thumbnail: model.thumbnail, category: model.category,
      sizes: model.sizes, price: model.price, discount: model.discount,
      collar: model.collar, sleeve: model.sleeve,
      tecidos: mapItems(model.tecidos, SUPABASE_TECIDOS),
      escudos: mapItems(model.escudos, SUPABASE_ESCUDOS),
      form: model.form || false
    })
    setIsEditModalOpen(true)
  }

  async function urlToBlob(url: any) { return (await fetch(url)).blob() }

  const handleSaveModelEdit = async () => {
    if (!editingModel) return
    ;(window as any)["stateLoadinScreen2"](true)
    const processList = (items: any[], prefix: string) => {
      if (!items) return []
      return items.map((item: any, i: number) => ({ key: `${prefix}${i + 1}`, file: item.file, existingUrl: item.existingUrl || null }))
    }
    const payload = {
      ...editingModel,
      tecidos: processList(editingModel.tecidos, "tecido"),
      escudos: processList(editingModel.escudos, "escudo"),
    }
    var thumb: string | undefined, blob: Blob | undefined
    if (editingModel.glb) {
      const url = URL.createObjectURL(editingModel.glb)
      thumb = await (window as any)["generateGLBThumbnail"](url) as string
      blob = await (await fetch(thumb!)).blob()
    } else if (editingModel.path && editingModel.thumbnailUpdate) {
      const b0 = await urlToBlob(editingModel.path)
      thumb = await (window as any)["generateGLBThumbnail"](URL.createObjectURL(b0)) as string
      blob = await (await fetch(thumb!)).blob()
    }
    edit3D(editingModel.id, payload, blob)

    // Update local state immediately — no reload needed
    setModels(prev => prev.map(m =>
      String(m.id) === String(editingModel.id)
        ? {
            ...m,
            name:     safeStr(editingModel.name)     || m.name,
            desc:     safeStr(editingModel.desc)     || m.desc,
            category: safeStr(editingModel.category) || m.category,
            sizes:    safeStr(editingModel.sizes)    || m.sizes,
            price:    safeStr(editingModel.price)    || m.price,
            discount: safeStr(editingModel.discount) || m.discount,
            collar:   safeStr(editingModel.collar)   || m.collar,
            sleeve:   safeStr(editingModel.sleeve)   || m.sleeve,
            form:     editingModel.form ?? m.form,
            ...(thumb ? { thumbnail: thumb } : {}),
          }
        : m
    ))

    // Also update selectedItem in the detail panel if it's the same model
    setSelectedItem(prev => {
      if (!prev || String((prev as any).id) !== String(editingModel.id)) return prev
      return {
        ...prev,
        name:     safeStr(editingModel.name)     || (prev as any).name,
        desc:     safeStr(editingModel.desc)     || (prev as any).desc,
        category: safeStr(editingModel.category) || (prev as any).category,
        ...(thumb ? { thumbnail: thumb } : {}),
      }
    })

    setIsEditModalOpen(false)
    setEditingModel(null)
  }

  const handleViewModel = (projectPath: string, model_id: any, simulator: any, name: any) => {
    $("#model-title span").text(name) as any
    ;(window as { [key: string]: any }).add_mode = false
    ;(window as { [key: string]: any }).currentProjectData = null

    const url = new URL(window.location.href)
    const removeKeys = ["x", "g", "t", "b", "n", "b1111", "u111", "ns", "tr", "pu", "pd", "p"]
    removeKeys.forEach(k => url.searchParams.delete(k))
    window.history.replaceState({}, "", url.toString())

    const path = projectPath.replace(".glb", ".svg")
    if (simulator) model_id = null
    ;(window as { [key: string]: any })["loadParts"](null, projectPath, true, 1, false, true, path, model_id)
  }

  const handleAddModel = (projectPath: string, model_id: any, simulator: any, name: any) => {
    ;(window as { [key: string]: any }).add_mode = true
    ;(window as { [key: string]: any }).nameMult = name
    let id = "0"
    if ((window as { [key: string]: any }).groupMore)
      id = String((window as { [key: string]: any }).groupMore.children.length)
    ;(window as { [key: string]: any }).sceneAPI.addObject({ id, name: name.replace("SIMULADOR -", ""), type: "Mesh" })
    const path = projectPath.replace(".glb", ".svg")
    ;(window as { [key: string]: any })["loadParts"](null, projectPath, true, 1, false, true, path, simulator ? null : model_id)
  }

  const handleDownloadModel = (projectPath: string) => {
    const path = projectPath.replace(".glb", ".svg")
    const cv = new fabric.Canvas("canvas").setDimensions({ width: 4096, height: 4096 })
    fabric.loadSVGFromURL(path, (objects, options) => {
      const obj = fabric.util.groupSVGElements(objects, options)
      obj.set({ left: 0, top: 0 }); cv.add(obj); cv.renderAll()
      exportPDF(cv)
    })
  }

  async function exportPDF(cv: any) {
    $("#svg-container").append(cv.toSVG())
    await new Promise((r) => setTimeout(r, 500))
    const pdf = new jsPDF("p", "mm", "a4", true) as any
    const w = pdf.internal.pageSize.getWidth()
    const el = document.getElementById("svg-container") as any
    el.firstElementChild.getBoundingClientRect()
    await pdf.svg(el.firstElementChild, { y: 0, x: 0, width: w, height: w })
      .then(() => { pdf.save("vetor.pdf") })
    $("#svg-container").empty()
  }

  // ─── Bulk SVG upload helpers (unchanged from ModelManager) ────────────────
  const handleBulkUploadClick = (model: Model) => { setBulkUploadModel(model); bulkFileInputRef.current?.click() }

  const handleBulkFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length || !bulkUploadModel) return
    e.target.value = ""
    if (files.length > 50) { alert(`Selecione até 50 arquivos.`); return }
    const model = bulkUploadModel
    setBulkUploadStatus({ active: true, current: 0, total: files.length, modelName: model.name })
    const saveFn = (window as any)["saveProjectFromManager"]
    for (let i = 0; i < files.length; i++) {
      const svgFile = files[i]; const projectName = svgFile.name.replace(".svg", "")
      setBulkUploadStatus(prev => ({ ...prev, current: i + 1 }))
      try {
        const THREE = (window as any).THREE
        const GLTFLoader = (window as any).THREE_GLTFLoader; const DRACOLoader = (window as any).THREE_DRACOLoader
        const SIZE = 1024
        const oc = document.createElement("canvas"); oc.width = oc.height = SIZE
        Object.assign(oc.style, { position: "fixed", opacity: "0", pointerEvents: "none", top: "-9999px" })
        document.body.appendChild(oc)
        const offRenderer = new THREE.WebGLRenderer({ canvas: oc, antialias: true, alpha: true })
        offRenderer.setSize(SIZE, SIZE); offRenderer.outputColorSpace = THREE.SRGBColorSpace; offRenderer.setClearColor(0, 0)
        const offScene = new THREE.Scene()
        const offCamera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000)
        offScene.add(new THREE.AmbientLight(0xffffff, 1.2))
        const dl = new THREE.DirectionalLight(0xffffff, 1.5); dl.position.set(1, 2, 3); offScene.add(dl)
        const dracoLoader = new DRACOLoader(); dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/")
        const gltf = await new Promise<any>((res, rej) => { const l = new GLTFLoader(); l.setDRACOLoader(dracoLoader); l.load(model.path, res, undefined, rej) })
        const ms = gltf.scene; offScene.add(ms)
        const box = new THREE.Box3().setFromObject(ms); const cen = new THREE.Vector3(); const sv = new THREE.Vector3()
        box.getCenter(cen); box.getSize(sv); ms.position.sub(cen)
        const md = Math.max(sv.x, sv.y, sv.z); const fr = (offCamera.fov * Math.PI) / 180
        const cd = ((md / 2) / Math.tan(fr / 2)) * 1.4
        offCamera.position.set(0, sv.y * 0.1, cd); offCamera.lookAt(0, 0, 0)
        await new Promise<void>((res, rej) => {
          const u = URL.createObjectURL(svgFile); const tl = new THREE.TextureLoader()
          tl.load(u, (tex: any) => {
            tex.colorSpace = THREE.SRGBColorSpace; tex.flipY = false; tex.needsUpdate = true
            ms.traverse((c: any) => { if (c.isMesh && (c.name || "").toLowerCase() !== "avatar") {
              const ap = (mat: any) => { mat.color = new THREE.Color(0xffffff); mat.map = tex; mat.map.flipY = false; mat.needsUpdate = true; mat.polygonOffset = true; mat.polygonOffsetFactor = -1; mat.polygonOffsetUnits = -1 }
              Array.isArray(c.material) ? c.material.forEach(ap) : ap(c.material)
            }})
            URL.revokeObjectURL(u); res()
          }, undefined, (err: any) => { URL.revokeObjectURL(u); rej(err) })
        })
        offRenderer.render(offScene, offCamera)
        const thumb3d = await new Promise<File>((res, rej) => oc.toBlob((b) => b ? res(new File([b], svgFile.name.replace(".svg", "_thumb.png"), { type: "image/png" })) : rej(), "image/png"))
        offRenderer.dispose(); dracoLoader.dispose(); document.body.removeChild(oc)
        const svgPreview = await new Promise<File>((res, rej) => {
          const u = URL.createObjectURL(svgFile); const img = new Image()
          img.onload = () => { const c2 = document.createElement("canvas"); c2.width = c2.height = 1024; const ctx = c2.getContext("2d")!; ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 1024, 1024); ctx.drawImage(img, 0, 0, 1024, 1024); URL.revokeObjectURL(u); c2.toBlob((b) => b ? res(new File([b], svgFile.name.replace(".svg", "_preview.webp"), { type: "image/webp" })) : rej(), "image/webp", 0.92) }
          img.onerror = () => { URL.revokeObjectURL(u); rej() }; img.src = u
        })
        const svgText = await svgFile.text(); const pako = (window as any).pako
        const svgSupa = new Blob([pako.gzip(svgText)], { type: "application/gzip" })
        if (typeof saveFn === "function") await saveFn(projectName, getUserId(), model.path, svgPreview, svgSupa, thumb3d, null, null, null, "", null, "", [], "", [])
      } catch (err) { console.error(`Erro ao salvar "${svgFile.name}":`, err) }
    }
    setBulkUploadStatus({ active: false, current: 0, total: 0, modelName: "" })
    setBulkUploadModel(null)
    alert(`✅ ${files.length} projeto(s) salvos com sucesso!`)
  }

  // ─── Derived sidebar categories based on active tab ──────────────────────
  const sidebarCategories = mainTab === "simulador" ? SIMULATOR_CATEGORIES : MANAGER_CATEGORIES

  // ─── RENDER ──────────────────────────────────────────────────────────────
  return (
    <ChangeSportErrorBoundary>
    <>
      {/* Hidden inputs & outside divs preserved from original */}
      <input ref={bulkFileInputRef} type="file" accept=".svg" multiple className="hidden" onChange={handleBulkFilesSelected} />

      {/* ── Global styles for JS-injected multiple-projects cards ── */}
      <style>{`
        #multiple-projects {
          display: none;
          flex-direction: column;
          inset: 0;
          z-index: 40;
          background: var(--secondary, #0f1117);
          overflow: hidden;
        }

        #multiple-projects-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          background: var(--background, #151820);
          flex-shrink: 0;
        }

        #multiple-projects-back {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.1);
          background: transparent;
          color: var(--primary, #e2e8f0);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
          flex-shrink: 0;
        }
        #multiple-projects-back:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.2);
        }

        #multiple-projects-list {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          display: grid !important;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          align-content: start;
        }

        /* ── Cards injetados pelo JS ── */
        #multiple-projects-list > div,
        #multiple-projects-list > .scene-card {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.08);
          background: var(--background, #151820);
          transition: border-color 0.2s, box-shadow 0.2s;
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }
        #multiple-projects-list > div:hover,
        #multiple-projects-list > .scene-card:hover {
          border-color: rgba(255,255,255,0.2);
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }

        /* Thumbnail / canvas dentro do card */
        #multiple-projects-list > div > canvas,
        #multiple-projects-list > div > img,
        #multiple-projects-list > div > .thumb,
        #multiple-projects-list > .scene-card > canvas,
        #multiple-projects-list > .scene-card > img {
          width: 100% !important;
          aspect-ratio: 4/3;
          object-fit: contain;
          background: #1a1d27;
          display: block;
        }

        /* Nome do card */
        #multiple-projects-list > div > p,
        #multiple-projects-list > div > .card-name,
        #multiple-projects-list > .scene-card > p {
          padding: 8px 12px 4px;
          font-size: 12px;
          font-weight: 600;
          color: var(--primary, #e2e8f0);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;
        }

        /* Barra de ações (botões VISUALIZAR / DELETAR) */
        #multiple-projects-list > div > .card-actions,
        #multiple-projects-list > div > div:last-child,
        #multiple-projects-list > .scene-card > div:last-child {
          display: flex;
          gap: 6px;
          padding: 6px 10px 10px;
        }

        /* Botões genéricos dentro dos cards */
        #multiple-projects-list button {
          flex: 1;
          padding: 6px 10px;
          border-radius: 8px;
          border: none;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s, filter 0.15s;
          letter-spacing: 0.02em;
        }
        #multiple-projects-list button:hover {
          filter: brightness(1.1);
        }

        /* Botão VISUALIZAR — verde */
        #multiple-projects-list button:not([class*="delet"]):not([class*="remov"]):not([class*="del"]) {
          background: #16a34a;
          color: #fff;
        }

        /* Botão DELETAR — vermelho */
        #multiple-projects-list button[class*="delet"],
        #multiple-projects-list button[class*="del-"],
        #multiple-projects-list button.delete,
        #multiple-projects-list button.deletar {
          background: #dc2626;
          color: #fff;
        }

        /* Scrollbar do painel */
        #multiple-projects-list::-webkit-scrollbar { width: 6px; }
        #multiple-projects-list::-webkit-scrollbar-track { background: transparent; }
        #multiple-projects-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 99px; }
      `}</style>

      {/* multiple-projects panel — layout padronizado, conteúdo injetado por JS */}
      <div id="multiple-projects">
        <div id="multiple-projects-header">
          <button id="multiple-projects-back">
            ← Voltar
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--primary, #e2e8f0)" }}>
            Cenas com Múltiplos
          </span>
        </div>
        <div id="multiple-projects-list"></div>
      </div>

      {/* Bulk upload banner */}
      {bulkUploadStatus.active && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-xl bg-blue-700 px-6 py-3 text-white shadow-2xl">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="font-medium">Salvando {bulkUploadStatus.current} de {bulkUploadStatus.total} — {bulkUploadStatus.modelName}</span>
          <span className="text-blue-200 text-sm">(não feche essa tela)</span>
        </div>
      )}

      {/* Edit Model Modal */}
      {isEditModalOpen && editingModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-[750px] max-h-[90vh] overflow-y-auto rounded-xl bg-background p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-primary">Editar Modelo</h2>
              <button onClick={() => setIsEditModalOpen(false)} className="rounded-full p-1 hover:bg-secondary transition-colors">
                <X className="h-5 w-5 text-primary" />
              </button>
            </div>
            <div className="space-y-4">
              {/* Informações Básicas */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Informações Básicas</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Nome da Modelagem</Label>
                    <Input className="text-primary" value={editingModel.name || ""} onChange={(e) => setEditingModel({ ...editingModel, name: e.target.value })} placeholder="Ex: Básico" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Descrição</Label>
                    <Input className="text-primary" value={editingModel.desc || ""} onChange={(e) => setEditingModel({ ...editingModel, desc: e.target.value })} placeholder="Ex: Básico com encaixe tradicional" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Categoria</Label>
                    <Input className="text-primary" value={editingModel.category || ""} onChange={(e) => setEditingModel({ ...editingModel, category: e.target.value })} placeholder="Ex: Futebol" />
                  </div>
                </div>
              </section>

              {/* Preço e Tamanhos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preço e Tamanhos</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tamanhos</Label>
                    <Input className="text-primary" value={editingModel.sizes || ""} onChange={(e) => setEditingModel({ ...editingModel, sizes: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Preço</Label>
                    <Input className="text-primary" value={editingModel.price || ""} onChange={(e) => setEditingModel({ ...editingModel, price: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Desconto</Label>
                    <Input className="text-primary" value={editingModel.discount || ""} onChange={(e) => setEditingModel({ ...editingModel, discount: e.target.value })} />
                  </div>
                </div>
              </section>

              {/* Arquivos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Arquivos</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Objeto (.glb)</Label>
                    <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-transparent cursor-pointer hover:bg-accent/50 transition-colors group">
                      <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm text-muted-foreground truncate flex-1">Escolher arquivo</span>
                      <input type="file" accept=".glb" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setEditingModel({ ...editingModel, glb: f }) }} />
                    </label>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Mapa SVG (.svg)</Label>
                    <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-transparent cursor-pointer hover:bg-accent/50 transition-colors group">
                      <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm text-muted-foreground truncate flex-1">Escolher arquivo</span>
                      <input type="file" accept="image/svg+xml" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setEditingModel({ ...editingModel, svg: f }) }} />
                    </label>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Atualizar Thumbnail</Label>
                    <label className="flex items-center gap-2 h-10 px-3 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 rounded border-input" checked={editingModel.thumbnailUpdate || false} onChange={(e) => setEditingModel({ ...editingModel, thumbnailUpdate: e.target.checked })} />
                      <span className="text-sm text-primary">Atualizar imagem</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Modelo */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Modelo</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tipo de Gola</Label>
                    <Input className="text-primary" value={editingModel.collar || ""} onChange={(e) => setEditingModel({ ...editingModel, collar: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tipo de Manga</Label>
                    <Input className="text-primary" value={editingModel.sleeve || ""} onChange={(e) => setEditingModel({ ...editingModel, sleeve: e.target.value })} />
                  </div>
                  <div className="flex items-end pb-0.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 rounded border-input" checked={editingModel.form || false} onChange={(e) => setEditingModel({ ...editingModel, form: e.target.checked })} />
                      <span className="text-sm text-primary">Habilitar Formulário</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Tecidos e Escudos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tecidos e Escudos</h4>
                <div className="grid grid-cols-2 gap-4">
                  <DynamicList label="Tecidos" items={(editingModel.tecidos ?? []) as any[]} setItems={(items: any) => setEditingModel({ ...editingModel, tecidos: items })} hasImage />
                  <DynamicList label="Escudos" items={(editingModel.escudos ?? []) as any[]} setItems={(items: any) => setEditingModel({ ...editingModel, escudos: items })} hasImage />
                </div>
              </section>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setIsEditModalOpen(false)} className="rounded-lg bg-secondary px-5 py-2 text-primary hover:opacity-80">Cancelar</button>
              <button onClick={handleSaveModelEdit} className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Model Modal (Gerenciador) ── */}
      {isNewModelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-[750px] max-h-[90vh] overflow-y-auto rounded-xl bg-background p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-primary">Adicionar Modelo</h2>
              <button onClick={() => { setIsNewModelOpen(false); resetNewModelForm() }} className="rounded-full p-1 hover:bg-secondary transition-colors">
                <X className="h-5 w-5 text-primary" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Informações Básicas */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Informações Básicas</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Nome da Modelagem *</Label>
                    <Input className="text-primary" value={newModelForm.name} onChange={(e) => setNewModelForm({ ...newModelForm, name: e.target.value })} placeholder="Ex: Básico" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Descrição (opcional)</Label>
                    <Input className="text-primary" value={newModelForm.desc} onChange={(e) => setNewModelForm({ ...newModelForm, desc: e.target.value })} placeholder="Ex: Encaixe tradicional" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Categoria (opcional)</Label>
                    <Input className="text-primary" value={newModelForm.category} onChange={(e) => setNewModelForm({ ...newModelForm, category: e.target.value })} placeholder="Ex: Futebol, Basquete" />
                  </div>
                </div>
              </section>

              {/* Preço e Tamanhos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preço e Tamanhos</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tamanhos</Label>
                    <Input className="text-primary" value={newModelForm.sizes} onChange={(e) => setNewModelForm({ ...newModelForm, sizes: e.target.value })} placeholder="Ex: P, M, G" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Preço</Label>
                    <Input className="text-primary" value={newModelForm.price} onChange={(e) => setNewModelForm({ ...newModelForm, price: e.target.value })} placeholder="Ex: R$ 199,90" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Desconto (separe por vírgula)</Label>
                    <Input className="text-primary" value={newModelForm.discount} onChange={(e) => setNewModelForm({ ...newModelForm, discount: e.target.value })} placeholder="Ex: 10, 20, 30" />
                  </div>
                </div>
              </section>

              {/* Arquivos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Arquivos</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Objeto (.glb) *</Label>
                    <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-transparent cursor-pointer hover:bg-accent/50 transition-colors group">
                      <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm text-muted-foreground truncate flex-1">
                        {newModelForm.obj ? newModelForm.obj.name : "Escolher arquivo"}
                      </span>
                      <input type="file" accept=".glb" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewModelForm({ ...newModelForm, obj: f }) }} />
                    </label>
                    {newModelForm.obj && <p className="text-xs text-green-400">✓ Arquivo selecionado</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Mapa SVG (.svg) *</Label>
                    <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-transparent cursor-pointer hover:bg-accent/50 transition-colors group">
                      <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm text-muted-foreground truncate flex-1">
                        {newModelForm.svg ? newModelForm.svg.name : "Escolher arquivo"}
                      </span>
                      <input type="file" accept="image/svg+xml" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewModelForm({ ...newModelForm, svg: f }) }} />
                    </label>
                    {newModelForm.svg && <p className="text-xs text-green-400">✓ Arquivo selecionado</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Thumbnail (imagem)</Label>
                    <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-transparent cursor-pointer hover:bg-accent/50 transition-colors group">
                      <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm text-muted-foreground truncate flex-1">
                        {newModelForm.thumbnail ? newModelForm.thumbnail.name : "Escolher arquivo"}
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewModelForm({ ...newModelForm, thumbnail: f }) }} />
                    </label>
                    {newModelForm.thumbnail && <p className="text-xs text-green-400">✓ Arquivo selecionado</p>}
                  </div>
                </div>
              </section>

              {/* Modelo */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Modelo</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tipo de Gola</Label>
                    <Input className="text-primary" value={newModelForm.collar} onChange={(e) => setNewModelForm({ ...newModelForm, collar: e.target.value })} placeholder="Ex: Gola V" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-primary">Tipo de Manga</Label>
                    <Input className="text-primary" value={newModelForm.sleeve} onChange={(e) => setNewModelForm({ ...newModelForm, sleeve: e.target.value })} placeholder="Ex: Raglan" />
                  </div>
                  <div className="flex items-end pb-0.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 rounded border-input" checked={newModelForm.form} onChange={(e) => setNewModelForm({ ...newModelForm, form: e.target.checked })} />
                      <span className="text-sm text-primary">Formulário</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Tecidos e Escudos */}
              <section className="bg-secondary/50 p-4 rounded-lg space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tecidos e Escudos</h4>
                <div className="grid grid-cols-2 gap-4">
                  <DynamicList label="Tecidos" items={newModelForm.tecidos} setItems={(items: any) => setNewModelForm({ ...newModelForm, tecidos: items })} hasImage />
                  <DynamicList label="Escudos" items={newModelForm.escudos} setItems={(items: any) => setNewModelForm({ ...newModelForm, escudos: items })} hasImage />
                </div>
              </section>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setIsNewModelOpen(false); resetNewModelForm() }} className="rounded-lg bg-secondary px-5 py-2 text-primary hover:opacity-80">
                Cancelar
              </button>
              <button
                onClick={handleSaveNewModel}
                disabled={isNewModelLoading || !isNewModelValid()}
                className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isNewModelLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Salvando...</> : "Salvar Modelo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          UNIFIED SCREEN  (id="simulator-projects" preserved for JS compat)
          ══════════════════════════════════════════════════════════════════════ */}
      <div id="simulator-projects" className={`flex h-screen bg-secondary text-primary overflow-hidden ${canvas}`}>

        {/* ── LEFT SIDEBAR — only visible in simulador ── */}
        {mainTab === "simulador" && (
        <aside className="w-60 flex-shrink-0 bg-background border-r border-white/10 flex flex-col overflow-hidden">

          {/* Categories header — only for simulador */}
          {mainTab === "simulador" && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <button
                className="flex items-center gap-2 font-semibold text-xs text-primary/60 uppercase tracking-wider w-full"
                onClick={() => setCategoriesOpen(!categoriesOpen)}
              >
                <span className="flex-1 text-left">Categorias</span>
                {categoriesOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </div>
          )}

          {/* Category list — only for simulador */}
          {mainTab === "simulador" && categoriesOpen && (
            <nav className="flex-1 overflow-y-auto py-1">
              {sidebarCategories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left
                    ${activeCategory === cat.key
                      ? "bg-blue-600/20 text-blue-400 font-medium"
                      : "text-primary/70 hover:bg-white/5 hover:text-primary"
                    }`}
                >
                  <span className="text-sm leading-none">{cat.icon}</span>
                  <span className="truncate">{cat.label}</span>
                  {cat.key === "Favoritos" && (
                    <span className="ml-auto text-xs bg-blue-600/30 text-blue-400 rounded-full px-1.5 py-0.5">
                      {favorites.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          )}

        </aside>
        )} {/* end simulador sidebar */}

        {/* ── MAIN CONTENT ── */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Top bar */}
          <header className="flex items-center gap-3 px-5 py-3 border-b border-white/10 bg-background flex-shrink-0">

            {/* Tab switcher — always visible in header */}
            {!isVendedor && (
              <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-secondary p-1 flex-shrink-0">
                <button
                  onClick={() => setMainTab("simulador")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors
                    ${mainTab === "simulador" ? "bg-blue-600 text-white" : "text-primary/60 hover:text-primary"}`}
                >
                  <Play className="h-3 w-3" /> Simulador
                </button>
                <button
                  onClick={() => setMainTab("gerenciador")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors
                    ${mainTab === "gerenciador" ? "bg-blue-600 text-white" : "text-primary/60 hover:text-primary"}`}
                >
                  <Package className="h-3 w-3" /> Modelagens
                </button>
              </div>
            )}

            {/* Back button (visible for simulator, maps to original id) */}


            {/* Search */}
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/40" />
              <input
                type="text"
                placeholder={mainTab === "simulador" ? "Buscar projetos por nome..." : "Buscar por nome, categoria ou tag..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-secondary py-2.5 pl-9 pr-4 text-sm text-primary placeholder:text-primary/40 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Sort 
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowSortMenu(!showSortMenu) }}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-secondary px-3 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors"
              >
                <span className="text-primary/60 text-xs">Ordenar</span>
                <span className="font-medium text-sm">{sortOrder}</span>
                <ChevronDown className="h-3.5 w-3.5 text-primary/60" />
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-1 z-30 w-44 rounded-lg border border-white/10 bg-background shadow-xl overflow-hidden">
                  {["Mais recentes", "Mais antigos", "Nome A-Z", "Nome Z-A"].map((opt) => (
                    <button key={opt} onClick={(e) => { e.stopPropagation(); setSortOrder(opt); setShowSortMenu(false) }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-white/5 ${sortOrder === opt ? "text-blue-400 font-medium" : "text-primary/70"}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>*/}

            {/* View toggle 
            <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-secondary p-1">
              <button onClick={() => setViewMode("grid")} className={`rounded-md p-1.5 transition-colors ${viewMode === "grid" ? "bg-blue-600 text-white" : "text-primary/50 hover:text-primary"}`}><Grid2X2 className="h-4 w-4" /></button>
              <button onClick={() => setViewMode("list")} className={`rounded-md p-1.5 transition-colors ${viewMode === "list" ? "bg-blue-600 text-white" : "text-primary/50 hover:text-primary"}`}><List className="h-4 w-4" /></button>
            </div>*/}

            {/* Action buttons */}
            {mainTab === "simulador" && (
              <button id="simulator-projects-mult"
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm text-white hover:bg-blue-700 transition-colors font-medium">
                <Plus className="h-4 w-4" /> Cenas Múltiplas
              </button>
            )}
            {mainTab === "gerenciador" && (
              <>
                <button id="briefings-access"
                  onClick={() => setShowBriefingsPage(true)}
                  className="relative flex items-center gap-1.5 rounded-lg border border-white/10 bg-secondary px-3 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors">
                  <ClipboardList className="h-4 w-4" /> Briefings
                  {briefingUnreadCount > 0 && (
                    <span className="flex items-center justify-center rounded-full bg-red-500 h-4 min-w-4 px-1 text-[10px] font-bold text-white">
                      {briefingUnreadCount}
                    </span>
                  )}
                </button>
                {!isVendedor && (
                  <button id="simulator-projects-mult"
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-secondary px-3 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors">
                    <Plus className="h-4 w-4" /> Cenas com Múltiplos
                  </button>
                )}
                {!isVendedor && (
                  <button id=""
                    onClick={() => setIsNewModelOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm text-white hover:bg-blue-700 transition-colors font-medium">
                    <Plus className="h-4 w-4" /> Novo Modelo
                  </button>
                )}
                {!isVendedor && (
                  <button
                    onClick={handleCreateSetup}
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-secondary px-3 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors">
                    <Layers className="h-4 w-4" /> Setup Modular
                  </button>
                )}
              </>
            )}
          </header>

          {/* Sub-tabs (manager only) */}
          {mainTab === "gerenciador" && !isVendedor && (
            <div className="flex items-center gap-1 px-5 py-2.5 border-b border-white/10 bg-background flex-shrink-0">
              {(["Todos", "Simulador", "Favoritos"] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveManagerTab(tab)}
                  className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors
                    ${activeManagerTab === tab ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "text-primary/60 hover:text-primary hover:bg-white/5"}`}>
                  {tab === "Favoritos" && <Star className="h-3.5 w-3.5" />}
                  {tab}
                  {tab === "Favoritos" && (
                    <span className="ml-0.5 text-xs bg-blue-600/30 text-blue-400 rounded-full px-1.5 py-0.5">{models.filter(m => m.isDefault).length}</span>
                  )}
                </button>
              ))}
              <span className="ml-auto text-xs text-primary/40">
                {filteredModels.length} modelo{filteredModels.length !== 1 ? "s" : ""} disponíveis
              </span>
            </div>
          )}

          {mainTab === "simulador" && (
            <div className="flex-shrink-0 border-b border-white/10 bg-background">
              {/* Explanatory banner */}
              <div className="mx-5 mt-3 mb-2 flex items-start gap-3 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3">
                <span className="text-lg leading-none flex-shrink-0 mt-0.5">🎨</span>
                <div>
                  <p className="text-xs font-semibold text-blue-400 leading-snug">Biblioteca de Modelos Base</p>
                  <p className="text-xs text-primary/50 mt-0.5 leading-relaxed">
                    Estes são os modelos disponíveis para sua loja. Clique em <span className="text-primary/70 font-medium">Adicionar ao Meu Catálogo</span> para incluí-los nos seus produtos e personalizá-los.
                  </p>
                </div>
              </div>
              <div className="px-5 pb-2">
                <span className="text-xs text-primary/40">
                  {filteredProjects.length} modelo{filteredProjects.length !== 1 ? "s" : ""} disponíve{filteredProjects.length !== 1 ? "is" : "l"}
                </span>
              </div>
            </div>
          )}

          {/* Grid + Detail panel */}
          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-5">

              {/* ══ SIMULATOR GRID ══ */}
              {mainTab === "simulador" && (
                <>
                  {filteredProjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-primary/40">
                      <p className="text-lg">Nenhum projeto encontrado</p>
                      {activeCategory === "Favoritos" && <p className="text-sm mt-1">Favorite alguns projetos para vê-los aqui</p>}
                    </div>
                  ) : (
                    <div className={`grid gap-4 ${selectedItem ? "grid-cols-2 xl:grid-cols-3" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"}`}>
                      {filteredProjects.map((project) => (
                        <div
                          key={`proj-${project.id}`}
                          onClick={() => setSelectedItem(project)}
                          className={`group relative overflow-hidden rounded-xl bg-background border transition-all cursor-pointer
                            ${(selectedItem as Project)?.id === project.id
                              ? "border-blue-500 shadow-lg shadow-blue-500/20"
                              : "border-white/10 hover:border-white/25 hover:shadow-md"
                            }`}
                        >
                          {/* Thumbnail */}
                          <div className="relative overflow-hidden aspect-square" style={{background:"#1a1d27"}}>
                            <img src={project.thumbnail || "/placeholder.svg"} alt={project.name}
                              className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />

                            {/* Already added badge */}
                            {savedTocatalog.has(project.id) && (
                              <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-green-600/90 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                                ✓ No catálogo
                              </span>
                            )}

                            {/* Category badge */}
                            <span className="absolute top-2 left-2 rounded-full bg-black/40 backdrop-blur-sm px-2 py-0.5 text-[10px] font-medium text-white">
                              {project.category}
                            </span>
                          </div>

                          {/* Card footer */}
                          <div className="p-3">
                            <p className="text-sm font-semibold text-primary truncate">{project.name}</p>
                            <p className="text-xs text-primary/40 mt-0.5 mb-2">ID: {project.id}</p>

                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openProject(project.id, project.path, project.name, project.model_id, project.metal, project.brilho, project.category, project.wiggle) }}
                                className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-white/5 py-1.5 text-xs font-medium text-primary/70 hover:text-primary hover:border-white/30 hover:bg-white/10 transition-all"
                              >
                                <Eye className="h-3.5 w-3.5" /> Visualizar
                              </button>

                              {savedTocatalog.has(project.id) ? (
                                <div className="flex-1 flex items-center justify-center gap-1 rounded-lg border border-green-500/30 bg-green-500/10 py-1.5 text-xs font-medium text-green-400">
                                  ✓ Modelagen
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSaveModel(project) }}
                                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-blue-500/40 bg-blue-600/10 py-1.5 text-xs font-medium text-blue-400 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                                >
                                  <SaveIcon className="h-3.5 w-3.5" /> + Modelagen
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ══ MANAGER GRID ══ */}
              {mainTab === "gerenciador" && (
                <>
                  {filteredModels.length === 0 && modularSetups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-primary/40">
                      <p className="text-lg">Nenhum modelo encontrado</p>
                      <p className="text-sm mt-1">"{searchTerm}"</p>
                    </div>
                  ) : (
                    <div id="model-manager" className={`grid gap-4 ${selectedItem ? "grid-cols-2 xl:grid-cols-3" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"}`}>
                      {filteredModels.map((model) => (
                        <div
                          key={`model-${model.id}`}
                          onClick={() => setSelectedItem(model)}
                          className={`group relative overflow-hidden rounded-xl bg-background border transition-all cursor-pointer
                            ${(selectedItem as Model)?.id === model.id
                              ? "border-blue-500 shadow-lg shadow-blue-500/20"
                              : "border-white/10 hover:border-white/25 hover:shadow-md"
                            }`}
                        >
                          <div className="relative aspect-square w-full overflow-hidden" style={{background:"#1a1d27"}}>
                            <img src={model.thumbnail || "/placeholder.svg"} alt={model.name}
                              className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />
                            {!isVendedor && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSetDefaultModel(model.id, model.path) }}
                                className={`absolute top-2 right-2 rounded-full p-1 transition-colors ${model.isDefault ? "opacity-100 text-yellow-400" : "opacity-0 group-hover:opacity-100 text-white/60 bg-black/30 hover:text-yellow-400"}`}>
                                <Star className={`h-4 w-4 ${model.isDefault ? "fill-yellow-400" : ""}`} />
                              </button>
                            )}
                            {model.simulator && (
                              <span className="absolute top-2 left-2 rounded-full bg-blue-600/80 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">Simulador</span>
                            )}
                          </div>
                          <div className="p-3">
                            {editingId === model.id ? (
                              <div className="flex gap-2">
                                <input type="text" value={editingName} onChange={(e) => setEditingName(e.target.value)}
                                  className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" autoFocus
                                  onKeyDown={(e) => { if (e.key === "Enter") handleSaveModelEdit(); if (e.key === "Escape") { setEditingId(null); setEditingName("") } }} />
                                <button onClick={handleSaveModelEdit} className="rounded bg-blue-600 px-2 py-1 text-xs text-white">✓</button>
                                <button onClick={() => { setEditingId(null); setEditingName("") }} className="rounded bg-secondary px-2 py-1 text-xs text-primary">✕</button>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-primary truncate">{model.name}</p>
                                  {!isVendedor && <p className="text-xs text-primary/40 mt-0.5">ID: {model.id}</p>}
                                </div>
                                {!isVendedor && (
                                  <div className="relative flex-shrink-0">
                                    <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === model.id ? null : model.id) }}
                                      className="rounded p-1 text-primary/40 hover:text-primary hover:bg-white/5 transition-colors">
                                      <MoreVertical className="h-4 w-4" />
                                    </button>
                                    {openMenuId === model.id && (
                                      <div className="absolute right-0 bottom-full mb-1 z-20 w-44 rounded-lg border border-white/10 bg-background shadow-xl overflow-hidden">
                                        <button onClick={(e) => { e.stopPropagation(); handleViewModel(model.path, model.id, model.simulator, model.name); setOpenMenuId(null) }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><Eye className="h-3.5 w-3.5" /> Visualizar</button>
                                        <button onClick={(e) => { e.stopPropagation(); handleAddModel(model.path, model.id, model.simulator, model.name); setOpenMenuId(null) }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><PlusIcon className="h-3.5 w-3.5" /> Adicionar2</button>
                                        {!isDesigner && (
                                          <button onClick={(e) => { e.stopPropagation(); setBriefingModalModel(model); setOpenMenuId(null) }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><ClipboardList className="h-3.5 w-3.5" /> Criar Briefing</button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); handleBulkUploadClick(model); setOpenMenuId(null) }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><Upload className="h-3.5 w-3.5" /> Upload SVGs</button>
                                        <button onClick={(e) => { e.stopPropagation(); handleStartEdit(model); setOpenMenuId(null) }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><Edit2 className="h-3.5 w-3.5" /> Editar</button>
                                        <div className="border-t border-white/10" />
                                        <button onClick={(e) => { e.stopPropagation(); model.simulator ? handleUnmarkModel(model.id, model.simulator, model.path) : handleDeleteModel(model.id, model.simulator, model.path); setOpenMenuId(null) }}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> {model.simulator ? "Remover" : "Deletar"}</button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {!isVendedor && (
                              <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewModel(model.path, model.id, model.simulator, model.name) }}
                                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                                >
                                  <Eye className="h-3.5 w-3.5" /> Abrir Projeto2
                                </button>
                              </div>
                            )}
                            {isVendedor && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setBriefingModalModel(model) }}
                                className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                              >
                                <ClipboardList className="h-3.5 w-3.5" /> Criar Briefing
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {modularSetups.length > 0 && (
                        <div className="col-span-full flex items-center gap-2 mt-2 mb-1">
                          <Layers className="h-4 w-4 text-purple-400" />
                          <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Setups Modulares</p>
                        </div>
                      )}

                      {modularSetups.map((setup) => (
                        <div
                          key={`setup-${setup.id}`}
                          onClick={() => setSelectedItem(setup)}
                          className={`group relative overflow-hidden rounded-xl bg-background border transition-all cursor-pointer
                            ${(selectedItem as Model)?.id === setup.id
                              ? "border-purple-500 shadow-lg shadow-purple-500/20"
                              : "border-white/10 hover:border-white/25 hover:shadow-md"
                            }`}
                        >
                          <div className="relative aspect-square w-full overflow-hidden" style={{background:"#1a1d27"}}>
                            <img src={setup.thumbnail || "/placeholder.svg"} alt={setup.name}
                              className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />
                            <span className="absolute top-2 left-2 rounded-full bg-purple-600/80 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                              <Layers className="h-3 w-3 inline mr-1" />Setup
                            </span>
                          </div>
                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-primary truncate">{setup.name}</p>
                                <p className="text-xs text-purple-400/60 mt-0.5">Setup Modular</p>
                              </div>
                              {!isVendedor && (
                                <div className="relative flex-shrink-0">
                                  <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === setup.id ? null : setup.id) }}
                                    className="rounded p-1 text-primary/40 hover:text-primary hover:bg-white/5 transition-colors">
                                    <MoreVertical className="h-4 w-4" />
                                  </button>
                                  {openMenuId === setup.id && (
                                    <div className="absolute right-0 bottom-full mb-1 z-20 w-44 rounded-lg border border-white/10 bg-background shadow-xl overflow-hidden">
                                      <button onClick={(e) => { e.stopPropagation(); handleOpenModularSetup(setup); setOpenMenuId(null) }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary/70 hover:text-primary hover:bg-white/5"><Eye className="h-3.5 w-3.5" /> Abrir no Editor</button>
                                      <div className="border-t border-white/10" />
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteModularSetup(setup); setOpenMenuId(null) }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> Deletar</button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>

            {/* ── DETAIL PANEL ── */}
            {selectedItem && (selectedItem as Model).category === "Setup Modular" && (
              <ChangeSportErrorBoundary>
              <aside className="w-60 flex-shrink-0 border-l border-white/10 bg-background overflow-y-auto flex flex-col">
                <div className="relative">
                  <button onClick={() => setSelectedItem(null)}
                    className="absolute top-2 right-2 z-10 rounded-full bg-black/40 p-1 text-white hover:bg-black/60 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                  <div className="aspect-square w-full flex items-center justify-center" style={{background:"#1a1d27"}}>
                    <img src={safeStr((selectedItem as any).thumbnail) || "/placeholder.svg"} alt={safeStr((selectedItem as any).name)}
                      className="h-full w-full object-contain" />
                  </div>
                  <span className="absolute top-2 left-2 rounded-full bg-purple-600/80 px-2 py-0.5 text-[10px] font-medium text-white">
                    <Layers className="h-3 w-3 inline mr-1" />Setup Modular
                  </span>
                </div>

                <div className="p-4 border-b border-white/10">
                  <h3 className="font-semibold text-primary text-sm leading-snug">{safeStr((selectedItem as any).name)}</h3>
                  <p className="text-xs text-purple-400/60 mt-1">Setup Modular</p>

                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleOpenModularSetup(selectedItem as Model)}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2 text-xs font-medium text-white hover:bg-blue-700 transition-colors">
                      <Play className="h-3.5 w-3.5" /> Abrir no Editor
                    </button>
                    <button onClick={() => handleDeleteModularSetup(selectedItem as Model)}
                      className="flex items-center justify-center rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 p-2 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="p-4 border-b border-white/10">
                  <h4 className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Peças do Setup</h4>
                  {modularParts.length > 0 ? (
                    <div className="space-y-3">
                      {modularParts.map((part, idx) => (
                        <div key={idx} className="rounded-lg border border-white/10 p-2">
                          <p className="text-xs font-medium text-primary mb-2 truncate">{part.name || `Peça ${idx + 1}`}</p>
                          <div className="space-y-1.5">
                            {(["position", "rotation", "scale"] as const).map((prop) => (
                              <div key={prop} className="flex items-center gap-1">
                                <span className="text-[10px] text-primary/40 w-7 flex-shrink-0">{prop === "position" ? "Pos" : prop === "rotation" ? "Rot" : "Scl"}</span>
                                {(["x", "y", "z"] as const).map((axis) => (
                                  <input key={axis} type="number" step={prop === "scale" ? "0.001" : "0.01"}
                                    value={parseFloat((part[prop] as any)[axis].toFixed(3))}
                                    onChange={(e) => {
                                      (window as any).updateModularPartTransform(idx, prop, axis, e.target.value)
                                      setModularParts((prev) => {
                                        const next = [...prev]
                                        next[idx] = { ...next[idx], [prop]: { ...next[idx][prop], [axis]: parseFloat(e.target.value) } }
                                        return next
                                      })
                                    }}
                                    className="w-full rounded border border-white/10 bg-background px-1 py-0.5 text-[10px] text-primary focus:border-purple-500 focus:outline-none"
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-primary/40">Abra o setup no editor para editar as peças.</p>
                  )}
                </div>

                {modularParts.length > 0 && (
                  <div className="p-4">
                    <button onClick={async () => { await (window as any).save_modular_transforms(); }}
                      className="w-full flex items-center justify-center gap-2 rounded-lg bg-purple-600 py-2 text-xs font-medium text-white hover:bg-purple-700 transition-colors">
                      <SaveIcon className="h-3.5 w-3.5" /> Salvar Transforms
                    </button>
                  </div>
                )}
              </aside>
              </ChangeSportErrorBoundary>
            )}

            {selectedItem && (selectedItem as Model).category !== "Setup Modular" && (
              <ChangeSportErrorBoundary>
              <aside className="w-60 flex-shrink-0 border-l border-white/10 bg-background overflow-y-auto flex flex-col">
                <div className="relative">
                  <button onClick={() => setSelectedItem(null)}
                    className="absolute top-2 right-2 z-10 rounded-full bg-black/40 p-1 text-white hover:bg-black/60 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                  <div className="aspect-square w-full flex items-center justify-center" style={{background:"#1a1d27"}}>
                    <img src={safeStr((selectedItem as any).thumbnail) || "/placeholder.svg"} alt={safeStr((selectedItem as any).name)}
                      className="h-full w-full object-contain" />
                  </div>
                  {mainTab === "simulador" && (selectedItem as Project).category && (
                    <span className="absolute top-2 left-2 rounded-full bg-black/40 backdrop-blur-sm px-2 py-0.5 text-[10px] font-medium text-white">{(selectedItem as Project).category}</span>
                  )}
                  {mainTab === "gerenciador" && (selectedItem as Model).simulator && (
                    <span className="absolute top-2 left-2 rounded-full bg-blue-600/80 px-2 py-0.5 text-[10px] font-medium text-white">Simulador</span>
                  )}
                </div>

                <div className="p-4 border-b border-white/10">
                  <h3 className="font-semibold text-primary text-sm leading-snug">{safeStr((selectedItem as any).name)}</h3>
                  <p className="text-xs text-primary/40 mt-1">ID: {safeStr((selectedItem as any).id)}</p>

                  <div className="flex gap-2 mt-3">
                    {mainTab === "simulador" ? (
                      <>
                        <button onClick={() => handleDownloadOnly((selectedItem as Project).path)}
                          title="Baixar SVG"
                          className="flex items-center justify-center rounded-lg border border-white/10 text-primary/50 hover:text-primary hover:border-white/25 p-2 transition-colors">
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleSetDefaultModel((selectedItem as Model).id, (selectedItem as Model).path)}
                          className={`flex items-center justify-center rounded-lg border p-2 transition-colors ${(selectedItem as Model).isDefault ? "border-yellow-500/40 text-yellow-400 bg-yellow-400/10" : "border-white/10 text-primary/50 hover:text-primary hover:border-white/25"}`}>
                          <Star className={`h-4 w-4 ${(selectedItem as Model).isDefault ? "fill-yellow-400" : ""}`} />
                        </button>
                        <button onClick={() => handleStartEdit(selectedItem as Model)}
                          className="flex items-center justify-center rounded-lg border border-white/10 text-primary/50 hover:text-primary hover:border-white/25 p-2 transition-colors">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDownloadModel((selectedItem as Model).path)}
                          className="flex items-center justify-center rounded-lg border border-white/10 text-primary/50 hover:text-primary hover:border-white/25 p-2 transition-colors">
                          <Download className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {mainTab === "simulador" ? (
                    <div className="mt-3 flex flex-col gap-2">
                      <button
                        onClick={() => openProject((selectedItem as Project).id, (selectedItem as Project).path, (selectedItem as any).name, (selectedItem as Project).model_id, (selectedItem as Project).metal, (selectedItem as Project).brilho, (selectedItem as Project).category, (selectedItem as Project).wiggle)}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" /> Visualizar
                      </button>
                      {savedTocatalog.has((selectedItem as Project).id) ? (
                        <div className="w-full flex items-center justify-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 py-2 text-xs font-medium text-green-400">
                          ✓ Adicionado ao catálogo
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSaveModel(selectedItem as Project)}
                          className="w-full flex items-center justify-center gap-2 rounded-lg border border-blue-500/40 bg-blue-600/10 py-2 text-xs font-medium text-blue-400 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                        >
                          <SaveIcon className="h-3.5 w-3.5" /> Adicionar ao Meu Catálogo
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleViewModel((selectedItem as Model).path, (selectedItem as Model).id, (selectedItem as Model).simulator, (selectedItem as any).name)}
                      className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                    >
                      <Play className="h-4 w-4" /> Abrir Projeto
                    </button>
                  )}
                </div>

                <div className="p-4">
                  <h4 className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-3">Informações</h4>
                  <div className="space-y-2.5">
                    {mainTab === "simulador" ? (
                      <>
                        {[
                          { label: "Categoria", value: (selectedItem as Project).category },
                          { label: "Criado em", value: (selectedItem as Project).createdAt },
                          { label: "Tipo", value: (selectedItem as Project).wiggle ? "Wiggle" : "Estático" },
                        ].map(({ label, value }) => (
                          <div key={label} className="flex items-start justify-between gap-2">
                            <span className="text-xs text-primary/50 flex-shrink-0">{label}</span>
                            <span className="text-xs text-primary font-medium text-right truncate max-w-[120px]">{value || "—"}</span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {[
                          { label: "Categoria", value: (selectedItem as Model).category },
                          { label: "Tipo", value: (selectedItem as Model).simulator ? "Simulador" : "Padrão" },
                          { label: "Gola", value: (selectedItem as Model).collar },
                          { label: "Manga", value: (selectedItem as Model).sleeve },
                          { label: "Tamanhos", value: (selectedItem as Model).sizes },
                          { label: "Preço", value: (selectedItem as Model).price },
                        ].map(({ label, value }) => (
                          <div key={label} className="flex items-start justify-between gap-2">
                            <span className="text-xs text-primary/50 flex-shrink-0">{label}</span>
                            <span className="text-xs text-primary font-medium text-right truncate max-w-[120px]">{value || "—"}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </aside>
              </ChangeSportErrorBoundary>
            )}
          </div>
        </div>
      </div>

      {/* my-projects div (ModelManager substituted) - hidden, controlled by JS */}
      <div className="h-full bg-secondary hidden" id="my-projects" />

      {/* Modal de criação de briefing, aberto a partir do 3-pontinhos da modelagem */}
      {briefingModalModel && (
        <BriefingModal
          model={briefingModalModel}
          onClose={() => setBriefingModalModel(null)}
          onSaved={() => { }}
        />
      )}

      {/* Tela de briefings (lista + comunicação vendedor <-> designer) */}
      {showBriefingsPage && (
        <BriefingsPage onClose={() => setShowBriefingsPage(false)} />
      )}

      {/* ── Modal Setup Modular ── */}
      {isSetupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-[700px] max-h-[85vh] overflow-y-auto rounded-xl bg-background p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-primary">
                {setupStep === "select" ? "Criar Setup Modular" : "Posicionar Modelos"}
              </h2>
              <button onClick={() => setIsSetupModalOpen(false)} className="rounded-full p-1 hover:bg-secondary transition-colors">
                <X className="h-5 w-5 text-primary" />
              </button>
            </div>

            {setupStep === "select" && (
              <>
                <div className="mb-4">
                  <Label className="text-primary">Nome do Setup</Label>
                  <Input
                    className="text-primary"
                    value={setupName}
                    onChange={(e) => setSetupName(e.target.value)}
                    placeholder="Ex: Uniforme Completo"
                  />
                </div>

                <p className="text-xs text-primary/50 mb-3">Selecione as modelagens que farão parte do setup:</p>

                <div className="grid grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto">
                  {models.filter(m => !m.simulator).map((model) => (
                    <div
                      key={model.id}
                      onClick={() => handleToggleSetupModel(model)}
                      className={`relative cursor-pointer rounded-lg border p-2 transition-all ${
                        selectedSetupModels.some(m => m.id === model.id)
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="aspect-square w-full overflow-hidden rounded-md bg-secondary mb-2">
                        <img src={model.thumbnail || "/placeholder.svg"} alt={model.name}
                          className="h-full w-full object-contain" />
                      </div>
                      <p className="text-xs font-medium text-primary truncate">{model.name}</p>
                      {selectedSetupModels.some(m => m.id === model.id) && (
                        <span className="absolute top-1 right-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
                          {selectedSetupModels.findIndex(m => m.id === model.id) + 1}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-4">
                  <span className="text-xs text-primary/40">{selectedSetupModels.length} modelo(s) selecionado(s)</span>
                  <div className="flex gap-2">
                    <button onClick={() => setIsSetupModalOpen(false)}
                      className="rounded-lg bg-secondary px-5 py-2 text-primary hover:opacity-80">Cancelar</button>
                    <button onClick={handleStartPositioning}
                      disabled={selectedSetupModels.length === 0}
                      className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                      Posicionar na Cena
                    </button>
                  </div>
                </div>
              </>
            )}

            {setupStep === "position" && (
              <div className="text-center py-8">
                <Layers className="h-12 w-12 mx-auto text-blue-400 mb-4" />
                <p className="text-sm text-primary mb-2">
                  Modelos adicionados na cena! Use os controles de transformação para posicionar.
                </p>
                <p className="text-xs text-primary/50 mb-4">
                  Quando estiver satisfeito com o posicionamento, clique em "Salvar Setup" no painel de múltiplos.
                </p>
                <button onClick={() => { setIsSetupModalOpen(false); setSetupStep("select") }}
                  className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700">
                  Entendido
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  </ChangeSportErrorBoundary>
  )
}

export default ChangeSport